import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Users, Building2, UserCircle, 
  Briefcase, FolderKanban, CheckSquare, MessageSquare, FileText, 
  Settings, LogOut, ChevronRight, Menu, X, BarChart3, Target, Zap
} from "lucide-react";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../../App";

// Orchestrio Console Layout Component
export const AdminLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { name: "Overview", href: "/admin", icon: LayoutDashboard },
    { name: "Lead Engine", href: "/admin/lead-engine", icon: Target, section: "Sales" },
    { name: "Leads", href: "/admin/leads", icon: Users, section: "Pipeline" },
    { name: "Companies", href: "/admin/companies", icon: Building2, section: "Pipeline" },
    { name: "Contacts", href: "/admin/contacts", icon: UserCircle, section: "Pipeline" },
    { name: "Deals", href: "/admin/deals", icon: Briefcase, section: "Pipeline" },
    { name: "Projects", href: "/admin/projects", icon: FolderKanban, section: "Delivery" },
    { name: "Tasks", href: "/admin/tasks", icon: CheckSquare, section: "Delivery" },
    { name: "Springboard (Prompt Generator)", href: "/admin/modules/springboard", icon: Zap, section: "Modules" },
    { name: "Client Mockups", href: "/admin/client-mockups", icon: Image, section: "Modules" },
    { name: "Comms", href: "/admin/inbox", icon: MessageSquare },
    { name: "Insights", href: "/admin/insights", icon: BarChart3 },
    { name: "Content", href: "/admin/blog", icon: FileText },
    { name: "Settings", href: "/admin/settings", icon: Settings },
  ];

  // Group navigation by sections
  const salesItems = navItems.filter(item => item.section === "Sales");
  const pipelineItems = navItems.filter(item => item.section === "Pipeline");
  const deliveryItems = navItems.filter(item => item.section === "Delivery");
  const modulesItems = navItems.filter(item => item.section === "Modules");
  const otherItems = navItems.filter(item => !item.section);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate("/admin/login");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  const isActive = (href) => {
    if (href === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#0E0E0E] flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#1C1C1C] border-r border-[#2E2E2E] transform transition-transform duration-200 lg:relative lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-[#2E2E2E]">
            <Link to="/admin" className="flex items-center gap-3">
              <img 
                src="/orchestrio-logo.png" 
                alt="Orchestrio" 
                className="h-6 w-auto"
              />
              <span className="text-xs font-medium text-[#8A8A8A] uppercase tracking-wider">Console</span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-[#8A8A8A] hover:text-[#F5F4EF]"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto scrollbar-custom">
            {/* Overview */}
            <Link
              to="/admin"
              className={`sidebar-link ${isActive("/admin") && location.pathname === "/admin" ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid="nav-overview"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Overview</span>
            </Link>

            {/* Sales Section */}
            <div className="mt-6 mb-2 px-4">
              <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Sales</span>
            </div>
            {salesItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            ))}

            {/* Pipeline Section */}
            <div className="mt-6 mb-2 px-4">
              <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Pipeline</span>
            </div>
            {pipelineItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            ))}

            {/* Delivery Section */}
            <div className="mt-6 mb-2 px-4">
              <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Delivery</span>
            </div>
            {deliveryItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            ))}

            {/* Modules Section */}
            <div className="mt-6 mb-2 px-4">
              <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">Modules</span>
            </div>
            {modulesItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-springboard`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            ))}

            {/* Other Items */}
            <div className="mt-6 mb-2 px-4">
              <span className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider">System</span>
            </div>
            {otherItems.filter(item => item.name !== "Overview").map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.name}</span>
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="border-t border-[#2E2E2E] p-4">
            <div className="flex items-center gap-3 mb-4">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
              ) : (
                <div className="w-10 h-10 bg-[#2E2E2E] rounded-full flex items-center justify-center">
                  <UserCircle className="w-6 h-6 text-[#8A8A8A]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-[#8A8A8A] truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8A8A8A] hover:text-[#F5F4EF] hover:bg-[#2E2E2E] transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-16 bg-[#1C1C1C] border-b border-[#2E2E2E] flex items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-[#8A8A8A] hover:text-[#F5F4EF]"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold">{title}</h1>
          </div>
          <Link 
            to="/" 
            target="_blank"
            className="text-sm text-[#8A8A8A] hover:text-[#F5F4EF] flex items-center gap-1"
          >
            View Site <ChevronRight className="w-4 h-4" />
          </Link>
        </header>

        {/* Page content */}
        <div className="flex-1 p-6 overflow-auto scrollbar-custom">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
